import React, { useEffect, useRef, useState } from 'react';
import { Formik } from 'formik';
import styles from '../../styles/scss/components/industrial/site-assessment-form.module.scss';
import { sendContact } from '../../services/contact';
import { signUp } from '../../services/email';
import {
  TextInput,
  Textarea,
  Select,
  Checkbox,
  ErrorSummary,
  FormStatus,
  SubmitButton,
  RequiredNote,
  bind,
  summaryErrors,
  phoneError,
  zipError,
  US_STATES,
  isValidEmail,
  formStyles as f,
} from '../form';

// WD-40 Brand Products list (same list the Contact Us opt-in uses)
const BRAND_LIST_ID = 17512397;

const INDUSTRIES = ['Metal Fabrication', 'Oil & Gas', 'Heavy Manufacturing', 'Food & Beverage', 'Transportation', 'Other'];
const EMPLOYEES = [
  { value: '0-50', text: '0-50' },
  { value: '51-250', text: '51-250' },
  { value: '251-500', text: '251-500' },
  { value: '501-1500', text: '501-1500' },
  { value: '1501-5000', text: '1501-5000' },
  { value: '5001-10000', text: '5001-10,000' },
  { value: '10000+', text: '10,000+' },
];
const YEARLY_USAGE = ['$0-$5,000', '$5,001-$10,000', '$10,001-$25,000', '$25,001-$50,000', '$50,001-$100,000', '$100,001+'];

const INITIAL_VALUES = {
  // Step 1 - Personal Information
  firstName: '',
  lastName: '',
  workEmail: '',
  jobTitle: '',
  phone: '',
  industry: '',
  // Step 2 - Company Information
  companyName: '',
  streetAddress: '',
  city: '',
  state: '',
  zipCode: '',
  // Step 3 - Additional Information
  numberOfEmployees: '',
  yearlyUsage: '',
  biggestConcern: '',
  additionalComments: '',
  // Opt-in must be an active choice, so it starts unchecked
  subscribeToEmails: false,
};

// An empty masked field focused at submit holds "(   )    -    "
const phoneOrBlank = (phone) => (/\d/.test(phone) ? phone : '');

const validateStep = (step, values) => {
  const errors = [];
  if (step === 1) {
    if (!values.firstName.trim()) errors.push({ field: 'firstName', message: 'First Name is required' });
    if (!values.lastName.trim()) errors.push({ field: 'lastName', message: 'Last Name is required' });
    if (!values.workEmail.trim()) errors.push({ field: 'workEmail', message: 'Work Email is required' });
    else if (!isValidEmail(values.workEmail)) errors.push({ field: 'workEmail', message: 'Invalid email address' });
    const phoneMsg = phoneError(values.phone);
    if (phoneMsg) errors.push({ field: 'phone', message: phoneMsg });
    if (!values.industry) errors.push({ field: 'industry', message: 'Industry is required' });
  }
  if (step === 2) {
    if (!values.companyName.trim()) errors.push({ field: 'companyName', message: 'Company Name is required' });
    const zipMsg = zipError(values.zipCode);
    if (zipMsg) errors.push({ field: 'zipCode', message: zipMsg });
  }
  return errors;
};

const SiteAssessmentForm = () => {
  // Which of the three steps is visible
  const [formStep, setFormStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  // When the step last changed; see the double-click guard below
  const stepChangedAt = useRef(0);

  // The thank-you replaces the (taller) form; move focus, and with it the
  // scroll position, to it so it isn't left above the viewport
  const thankYouRef = useRef(null);
  useEffect(() => {
    if (submitted && thankYouRef.current) thankYouRef.current.focus();
  }, [submitted]);

  // "Submit another request" removes the button that had focus; put focus
  // on the form's heading instead of letting it fall back to the page top
  const titleRef = useRef(null);
  const restarted = useRef(false);
  useEffect(() => {
    if (!submitted && restarted.current && titleRef.current) titleRef.current.focus();
    restarted.current = submitted;
  }, [submitted]);

  const goToStep = (step) => {
    stepChangedAt.current = Date.now();
    setSubmitError(false);
    setFormStep(step);
  };

  // Next, Back and Submit share one spot: a double-click (or double Enter)
  // would otherwise submit the step that just appeared (flashing its errors,
  // skipping it, or on step 3 sending the request unseen). Ignore submits
  // for a moment after every step change, before Formik validates.
  const guardedSubmit = (formik) => (e) => {
    if (Date.now() - stepChangedAt.current < 500) {
      e.preventDefault();
      return;
    }
    formik.handleSubmit(e);
  };

  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    // Steps 1-2: advance. resetForm keeps the values (so Back/Next never
    // loses answers) and clears submitCount so the next step starts clean.
    if (formStep < 3) {
      resetForm(values);
      goToStep(formStep + 1);
      return;
    }
    setSubmitError(false);
    const lists = values.subscribeToEmails ? [BRAND_LIST_ID] : [];
    // Keys the contactUs Lambda reads for topic "offer" (addr1/city/state/zip)
    const submissionData = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.workEmail,
      phone: phoneOrBlank(values.phone),
      jobTitle: values.jobTitle,
      company: values.companyName,
      addr1: values.streetAddress,
      city: values.city,
      state: values.state,
      zip: values.zipCode,
      industry: values.industry,
      numberOfEmployees: values.numberOfEmployees,
      yearlyUsage: values.yearlyUsage,
      biggestConcern: values.biggestConcern,
      message: values.additionalComments,
      lists,
      topic: 'offer',
      env: process.env.GATSBY_DATOCMS_ENV
    };

    // The thank-you panel replaces the form; "Submit another request"
    // remounts Formik with INITIAL_VALUES, so there's nothing to reset here
    const done = () => {
      setSubmitted(true);
      setFormStep(1);
    };

    sendContact(submissionData)
      .then(() => {
        if (!lists.length) return done();
        // 'contact' is the identifier the subscribe Lambda treats as a Brand signup
        return signUp({
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.workEmail,
          phone: phoneOrBlank(values.phone),
          lists,
          industry: '',
          industrytime: '',
          birthday: '',
          userplus18yearsold: true,
          confirmation: true,
          optSubject: '',
          optMessage: '',
          signupFormID: 1828532,
          formIdentifier: 'contact'
        }).then(done, (err) => {
          // The assessment request already went through; a failed newsletter
          // signup must not invite a duplicate resubmission
          console.log('Newsletter signup failed:', err);
          done();
        });
      })
      .catch((err) => {
        console.log('Error submitting form:', err);
        setSubmitting(false);
        setSubmitError(true);
      });
  };

  const containerClass = submitted ? styles.thankYouPageContainer : styles.formContainer;

  return (
    <div id="site-assessment-form" className={containerClass}>
      {!submitted && <h2 className={styles.formTitle} tabIndex={-1} ref={titleRef}>REQUEST A SITE ASSESSMENT</h2>}

      {/* Show success message if form was submitted successfully */}
      {submitted && (
        <div className={styles.thankYouWrapper}>
          <div className={styles.thankYouContainer}>
            <div className={styles.thankYouContent}>
              <h2 className={styles.thankYouTitle} tabIndex={-1} ref={thankYouRef}>Thank You!</h2>
              <p className={styles.thankYouText}>
                Now, that wasn't so painful, was it?
              </p>
              <p className={styles.thankYouDescription}>
                Remember to check out the other cool stuff we have that you can discover yourself, like the Solution
                Explorer on the new WD-40® Brand website. There, you can search for ways WD-40 Brand products can
                help you get the job done at Home, at Work and at Play. We have specific uses, videos and recommended
                products, too.
              </p>
              
              <div className={styles.thankYouButtons}>
                <a href="/products" className={styles.thankYouButton}>
                  PRODUCTS
                </a>
                <a href="/blog" className={styles.thankYouButton}>
                  BLOG
                </a>
                <button 
                  onClick={() => setSubmitted(false)} 
                  className={styles.thankYouButton}
                >
                  SUBMIT ANOTHER REQUEST
                </button>
              </div>
            </div>
            
            <div className={styles.contactSidebar}>
              <div className={styles.contactSection}>
                <h3 className={styles.contactTitle}>PHONE:</h3>
                <div className={styles.contactInfo}>
                  <p className={styles.contactLabel}>WD-40 CUSTOMER RELATIONS</p>
                  <p className={styles.contactValue}>1.888.324.7596</p>
                </div>
              </div>
              
              <div className={styles.contactSection}>
                <h3 className={styles.contactTitle}>MAILING ADDRESS:</h3>
                <div className={styles.contactInfo}>
                  <p className={styles.contactLabel}>WD-40 CUSTOMER RELATIONS</p>
                  <p className={styles.contactValue}>P.O. BOX 308</p>
                  <p className={styles.contactValue}>BUDD LAKE, NJ, 07828</p>
                </div>
              </div>
              
              <div className={styles.contactSection}>
                <h3 className={styles.contactTitle}>INVESTOR RELATIONS:</h3>
                <div className={styles.contactInfo}>
                  <p className={styles.contactLabel}>WENDY KELLEY</p>
                  <p className={styles.contactValue}>1.888.324.7596</p>
                  <p className={styles.contactValue}>INVESTORRELATIONS@WD40.COM</p>
                </div>
              </div>
              
              <div className={styles.contactDisclaimer}>
                <p>* All customer inquiries should be directed to Customer Relations at the phone number or mailing address listed. Product inquiries sent to this email address will not receive a response.</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {!submitted && (
        <Formik
          initialValues={INITIAL_VALUES}
          validate={(values) => validateStep(formStep, values)}
          onSubmit={handleSubmit}
        >
          {(props) => (
            <form className={`${f.form} ${styles.formPanel}`} onSubmit={guardedSubmit(props)} noValidate>
              <p className={f.help} aria-live="polite">Step {formStep} of 3</p>
              {formStep < 3 && <RequiredNote />}
              <ErrorSummary errors={summaryErrors(props)} submitCount={props.submitCount} />
              <FormStatus status={submitError ? 'error' : null} />

              {formStep === 1 && (
                <>
                  <div className={f.row}>
                    <TextInput label="First Name" required autoComplete="given-name" {...bind(props, 'firstName')} />
                    <TextInput label="Last Name" required autoComplete="family-name" {...bind(props, 'lastName')} />
                  </div>
                  <TextInput label="Work Email" type="email" required autoComplete="email" {...bind(props, 'workEmail')} />
                  <div className={f.row}>
                    <TextInput label="Job Title" autoComplete="organization-title" {...bind(props, 'jobTitle')} />
                    <TextInput label="Phone" type="tel" mask="phone" autoComplete="tel-national" placeholder="(000) 000-0000" {...bind(props, 'phone')} />
                  </div>
                  <Select label="Industry" required placeholder="Select Industry" options={INDUSTRIES} {...bind(props, 'industry')} />
                </>
              )}

              {formStep === 2 && (
                <>
                  <TextInput label="Company Name" required autoComplete="organization" {...bind(props, 'companyName')} />
                  <TextInput label="Street Address" autoComplete="street-address" {...bind(props, 'streetAddress')} />
                  <div className={`${f.row} ${f.row3}`}>
                    <TextInput label="City" autoComplete="address-level2" {...bind(props, 'city')} />
                    <Select label="State" placeholder="Select a State" options={US_STATES} {...bind(props, 'state')} />
                    <TextInput label="Zip Code" autoComplete="postal-code" {...bind(props, 'zipCode')} />
                  </div>
                </>
              )}

              {formStep === 3 && (
                <>
                  <div className={f.row}>
                    <Select label="Number of Employees" placeholder="Select Number of Employees" options={EMPLOYEES} {...bind(props, 'numberOfEmployees')} />
                    <Select label="Yearly Usage" placeholder="Select Yearly Usage" options={YEARLY_USAGE} {...bind(props, 'yearlyUsage')} />
                  </div>
                  <TextInput label="Biggest Concern" {...bind(props, 'biggestConcern')} />
                  <Textarea label="Additional Comments" {...bind(props, 'additionalComments')} />
                  <Checkbox
                    name="subscribeToEmails"
                    className={f.field}
                    label={<>Sign me up for emails for: WD-40<sup>&reg;</sup> Brand Products</>}
                    checked={props.values.subscribeToEmails}
                    onChange={(e) => props.setFieldValue('subscribeToEmails', e.target.checked)}
                  />
                </>
              )}

              <div className={f.actions}>
                {formStep > 1 && (
                  <button type="button" className={f.secondary} onClick={() => { props.resetForm(props.values); goToStep(formStep - 1); }}>
                    Back
                  </button>
                )}
                <SubmitButton busy={props.isSubmitting}>{formStep < 3 ? 'Next' : 'Submit'}</SubmitButton>
              </div>
            </form>
          )}
        </Formik>
      )}
    </div>
  );
};

export default SiteAssessmentForm;
